import {ReactElement, useState} from "react";
import {Field, Label, Message} from "@zendeskgarden/react-forms";
import {CustomZDDropdown} from "../../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {
    PayrollDefaultLevel,
    useListPayrollJurisdictionsQuery,
} from "../../../../__generated__/generated_types";
import DefaultsForm from "./DefaultsForm";
import styles from "./PayrollDefaults.module.scss";
import DefaultsFormWithInitialData from "./DefaultsFormWithInitialData";
import {Inline} from "@zendeskgarden/react-loaders";

interface JurisdictionDefaultsFormProps {
    positionIds: string[];
}

export default function JurisdictionDefaultsForm(props: JurisdictionDefaultsFormProps): ReactElement {
    const {positionIds} = props;
    const [selectedJurisdictionIds, setSelectedJurisdictionIds] = useState<string[]>([]);

    const {data: jurisdictionsData, loading: jurisdictionsDataLoading} = useListPayrollJurisdictionsQuery({
        fetchPolicy: "no-cache"
    });
    const jurisdictions = (jurisdictionsData?.listPayrollJurisdictions ?? []).filter(j => !j.disabled);
    const selectedPositionId = positionIds[0] ?? "";
    const selectedJurisdictionId = selectedJurisdictionIds[0] ?? "";
    const canLoadInitialData = positionIds.length === 1
        && selectedPositionId
        && selectedJurisdictionIds.length === 1
        && selectedJurisdictionId;

    if(positionIds.length === 0) {
        return <div className={styles.positionSelectorMessage}>Select a position to configure defaults.</div>;
    }

    return (
        <>
            <div className={styles.jurisdictionSelector}>
                <Field className={styles.field}>
                    <Label className={styles.label}>Jurisdiction</Label>
                    <CustomZDDropdown
                        widthSpec={"100%"}
                        applySelectedItems={(items) => {
                            const values = Array.isArray(items) ? items : [items];
                            setSelectedJurisdictionIds(values.filter(Boolean) as string[]);
                        }}
                        initialSelectedItems={selectedJurisdictionIds}
                        openDropdownPlaceholder="Select Jurisdiction"
                        closedDropdownPlaceholder=""
                        options={jurisdictions.map(j => ({value: j.id, label: j.name}))}
                        isError={false}
                        onBlur={() => undefined}
                        isMulti
                        allOption
                        noSearchIcon
                    />
                    {selectedJurisdictionIds.length > 1 &&
                        <Message className={styles.selectorMessage} validation="warning">
                            Saving will overwrite existing defaults for all selected jurisdictions.
                        </Message>
                    }
                    {selectedJurisdictionIds.length === 0 && !jurisdictionsDataLoading &&
                        <Message className={styles.selectorMessage}>
                            Select a jurisdiction to configure defaults.
                        </Message>
                    }
                    {jurisdictionsDataLoading &&
                        <Message className={styles.selectorMessage}>
                            <Inline />
                        </Message>
                    }
                </Field>
            </div>
            {canLoadInitialData ?
                <DefaultsFormWithInitialData
                    positionId={selectedPositionId}
                    defaultLevel={PayrollDefaultLevel.Jurisdiction}
                    jurisdictionId={selectedJurisdictionId}
                /> :
                selectedJurisdictionIds.length > 0 ?
                    <DefaultsForm
                        positionIds={positionIds}
                        defaultLevel={PayrollDefaultLevel.Jurisdiction}
                        jurisdictionIds={selectedJurisdictionIds}
                        showResetControls={false}
                    /> :
                    <></>
            }
        </>
    );
}
